import styled from "styled-components";
import BaseLayout from "common/Layouts/Layout";
import Card from "common/Boxes/Card";
import React, { CSSProperties, ReactNode, useContext } from "react";
import useMedia from "utils/hooks/useMedia";
import FiltersColumn from "common/Filters/FiltersColumn/FiltersColumn";
import { PageWrapperContext } from "common/Home/PageWrapper.context";
import { MOBILE_FILTERS, SMALL_MOBILE } from "common/constants";
import { OverlayWrapper } from "common/Overlays/Overlay";
import { HEADER_HEIGHT } from "common/Home/constants";

interface TwoChartsTableLayoutProps {
  smallChartUpperLeft: ReactNode;
  largeChartUpperMiddle: ReactNode;
  mainContent: ReactNode;
  mobileCharts: ReactNode[];
  useMobileCards?: boolean;
  style?: CSSProperties;
  filtersExpanded?: boolean;
  topRowHeight?: number | string;
  distanceFromHeader?: number;
  hideCharts?: boolean;
  hideLargeChartCard?: boolean;
}

const TwoChartsTableLayout = ({
  smallChartUpperLeft,
  largeChartUpperMiddle,
  mainContent,
  mobileCharts,
  useMobileCards = true,
  style,
  filtersExpanded = true,
  topRowHeight,
  distanceFromHeader = 0,
  hideCharts,
  hideLargeChartCard,
}: TwoChartsTableLayoutProps) => {
  const { distanceTop } = useContext(PageWrapperContext);
  const padding = useMedia([`(max-width: ${SMALL_MOBILE})`], [12], 16);

  const { filters } = useContext(PageWrapperContext);
  const mainContentList = Array.isArray(mainContent)
    ? mainContent
    : [mainContent];

  const isMobile = useMedia(
    [`(max-width: ${MOBILE_LAYOUT_ONE_COLUMN})`],
    [true],
    false
  );
  const filtersOnGrid = useMedia(
    [`(min-width: calc(${MOBILE_FILTERS} + 1px))`],
    [true],
    false
  );

  const useCardGridItems = !isMobile || useMobileCards;
  const GridItemComponent = useCardGridItems ? CardGridItem : GridItem;
  const LargeChartGridItemComponent =
    useCardGridItems && !hideLargeChartCard ? CardGridItem : GridItem;
  const mobileGridItems = mobileCharts.map((el, index) => {
    return (
      <GridItemComponent
        $gridArea={`${MOBILE_CHART}_${index}`}
        key={`${MOBILE_CHART}_${index}`}
      >
        {el}
      </GridItemComponent>
    );
  });

  const renderDesktopGridLayout = () => (
    <>
      {!hideCharts && (
        <>
          <GridItemComponent $gridArea={SMALL_CHART_LEFT}>
            {smallChartUpperLeft}
          </GridItemComponent>
          <LargeChartGridItemComponent $gridArea={LARGE_CHART_MIDDLE}>
            {largeChartUpperMiddle}
          </LargeChartGridItemComponent>
        </>
      )}
      {mainContentList.map((mainContentComponent, index) => (
        <GridItemComponent key={index} $gridArea={getMainContentArea(index)}>
          {mainContentComponent}
        </GridItemComponent>
      ))}
    </>
  );

  const contentDistanceTop =
    (distanceTop || 0) +
    distanceFromHeader +
    HEADER_HEIGHT +
    (distanceFromHeader ? 0 : padding);
  const contentHeight = `calc(100vh - ${contentDistanceTop}px - ${padding}px)`;

  return (
    <ContentAndFiltersWrapper style={style} $padding={padding}>
      <ResponsiveLayout
        $numMainContentRows={
          isMobile ? mobileGridItems.length : mainContentList.length
        }
        $useCardGridItems={useCardGridItems}
        $topRowHeight={topRowHeight}
        $hideCharts={hideCharts}
        style={{ minHeight: contentHeight }}
      >
        {!isMobile ? renderDesktopGridLayout() : mobileGridItems}
      </ResponsiveLayout>
      {filters && filtersOnGrid && (
        <FiltersColumn
          expanded={filtersExpanded}
          filtersSelectors={filters.selectors}
          filtersActions={filters.actions}
          width={FILTERS_WIDTH}
          height={contentHeight}
          selectLoadingStates={filters.loadingStatesSelector}
          hideFilterTypes={filters.hideFilterTypes}
          filterTypeToTitle={filters.filterTypeToTitle}
          isLoading={filters.loading}
          style={{ paddingLeft: 16 }}
        />
      )}
    </ContentAndFiltersWrapper>
  );
};

export default TwoChartsTableLayout;

export const MOBILE_LAYOUT_ONE_COLUMN = "650px";
const SMALL_CHART_LEFT = "smallChartLeft";
const LARGE_CHART_MIDDLE = "largeChartMiddle";
const MOBILE_CHART = "mobileChart";
const FILTERS_RIGHT = "filtersRight";
const MAIN_CONTENT = "mainContent";
const getMainContentArea = (index: number) => MAIN_CONTENT + "_" + index;
const getMobileMainContentArea = (index: number) => MOBILE_CHART + "_" + index;
export const GRID_GAP = 16;
const FILTERS_WIDTH = 280;

const getMainContentAreas = (
  numMainContentRows: number,
  columnCount: number
) => {
  return Array(numMainContentRows)
    .fill(0)
    .map((_, index) => {
      const areaName = getMainContentArea(index);
      if (columnCount === 1) {
        return `"${areaName}"`;
      } else if (columnCount === 2) return `"${areaName} ${areaName}"`;
      else if (columnCount === 3) {
        return `"${areaName} ${areaName} ${FILTERS_RIGHT}"`;
      }
    })
    .join("\n");
};

const getMobileMainContentAreas = (numMainContentRows: number) =>
  Array(numMainContentRows)
    .fill(0)
    .map((_, index) => {
      const areaName = getMobileMainContentArea(index);
      return `"${areaName}"`;
    })
    .join("\n");

const ContentAndFiltersWrapper = styled.div<{ $padding: number }>`
  padding: ${(p) => p.$padding}px;
  display: grid;
  grid-template-columns: 1fr min-content;
`;
const ResponsiveLayout = styled(BaseLayout)<{
  $topRowHeight?: number | string;
  $numMainContentRows: number;
  $useCardGridItems?: boolean;
  $hideCharts?: boolean;
}>`
  padding: 0;
  grid-gap: ${GRID_GAP}px;
  grid-template-columns: 300px 1fr;
  grid-template-rows: ${(p) => {
    const secondRow = `repeat(${p.$numMainContentRows}, auto)`;
    if (p.$hideCharts) return secondRow;
    let topRowHeight = "400px";
    if (typeof p.$topRowHeight === "string") topRowHeight = p.$topRowHeight;
    else if (typeof p.$topRowHeight === "number")
      topRowHeight = `${p.$topRowHeight}px`;
    return `${topRowHeight} ${secondRow}`;
  }};
  grid-template-areas: ${(p) => {
    const secondRow = `${getMainContentAreas(p.$numMainContentRows, 2)}`;
    if (p.$hideCharts) return secondRow;
    return `"${SMALL_CHART_LEFT} ${LARGE_CHART_MIDDLE}" ${secondRow}`;
  }};

  @media (max-width: ${MOBILE_LAYOUT_ONE_COLUMN}) {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: ${(p) => {
      let topRowHeight = "330px";
      if (typeof p.$topRowHeight === "string") topRowHeight = p.$topRowHeight;
      else if (typeof p.$topRowHeight === "number")
        topRowHeight = `${p.$topRowHeight}px`;
      return `${topRowHeight} repeat(${p.$numMainContentRows}, auto)`;
    }};
    grid-template-areas: ${(p) =>
      `${getMobileMainContentAreas(p.$numMainContentRows)}`};
  }

  ${(p) =>
    !p.$useCardGridItems &&
    `
    ${OverlayWrapper} {
      background: ${p.theme.colors.layoutBgColor};
    }
  `};
`;
const CardGridItem = styled(Card)<{ $gridArea?: string }>`
  grid-area: ${(p) => p.$gridArea};
  position: relative;
  display: flex;
  flex-direction: column;
`;
const GridItem = styled.div<{ $gridArea?: string }>`
  grid-area: ${(p) => p.$gridArea};
  position: relative;
  display: flex;
  flex-direction: column;
`;
